/**
 * Alta y edicion de un banner de portada (ADM-05).
 *
 * Vive en un modal porque cambiar banners es cosa de campana —unas pocas veces
 * al ano— y la lista es lo que se mira siempre. Es el mismo criterio del alta de
 * administradores.
 *
 * **La pieza se sube, ya no se pega una ruta (ADM-04).** La subida es un paso
 * aparte del guardado, y a proposito: devuelve la clave y las MEDIDAS, que son
 * las dos cosas que el formulario no puede saber por su cuenta. Antes las media
 * el navegador con la vista previa; ahora no serviria, porque lo que se publica
 * no es el archivo que se eligio —el servidor lo endereza, lo achica a 1920 px
 * de lado mayor y lo convierte a WebP— y medir el original daria el tamaño de
 * algo que nadie va a descargar.
 *
 * En una edicion la imagen es OPCIONAL: sin pieza nueva, el DTO viaja sin
 * `clave`, `ancho` ni `alto`, y el banner conserva la que tenia. Es para lo que
 * `ActualizarBannerDto` es parcial de verdad. En un alta, en cambio, no se
 * puede guardar sin imagen: un banner sin pieza no es un banner.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import type {
  ActualizarBannerDto,
  Banner,
  CrearBannerDto,
  ImagenSubidaDto,
  UbicacionBanner,
} from '@elcarpintero/compartido';

import { useSubidaImagen } from '../../hooks/use-subida-imagen';
import { subirImagenBanner } from '../../servicios/portada.servicio';
import { NOMBRE_UBICACION, UBICACIONES } from '../../utilidades/banners';
import { Icono } from '../comunes/Icono';
import { CampoPiezaMovil, type VistaPreviaPieza } from './CampoPiezaMovil';
import { Modal } from './Modal';
import { SubidorImagen } from './SubidorImagen';

/** Que pieza espera cada fila, para que el administrador suba la medida correcta. */
const AYUDA_UBICACION: Record<UbicacionBanner, string> = {
  carrusel: 'La lámina grande de arriba, de lado a lado. Piezas de 16:5, por ejemplo 1920 x 600.',
  promociones:
    'Tres bloques bajo el carrusel: se alternan una pieza ancha (1600 x 880) y dos angostas (800 x 880). Cada bloque rota entre las que le tocan.',
  cuadros:
    'Cuatro cuadros de 760 x 660 con el título y la bajada escritos encima: la imagen va sin texto.',
};

/**
 * Dar de alta y editar salen por puertas distintas, y no es ceremonia: en un
 * alta la imagen es obligatoria y en una edicion es opcional —sin pieza nueva,
 * el banner conserva la que tenia—, asi que los dos datos NO son del mismo
 * tipo. Con un solo callback habria que mentirle al compilador con un `as` en
 * cada alta; con dos, cada uno lleva exactamente lo que puede llevar.
 */
export interface FormularioBannerProps {
  /** `null` es un alta; con banner es una edicion. */
  banner: Banner | null;
  abierto: boolean;
  enviando: boolean;
  falla: string | null;
  onCerrar(): void;
  onCrear(datos: CrearBannerDto): void;
  onActualizar(datos: ActualizarBannerDto): void;
}

function vacioANulo(valor: string): string | null {
  const limpio = valor.trim();
  return limpio.length > 0 ? limpio : null;
}

export function FormularioBanner({
  banner,
  abierto,
  enviando,
  falla,
  onCerrar,
  onCrear,
  onActualizar,
}: FormularioBannerProps) {
  const [ubicacion, setUbicacion] = useState<UbicacionBanner>('carrusel');
  const [textoAlternativo, setTextoAlternativo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [bajada, setBajada] = useState('');
  const [enlace, setEnlace] = useState('');
  /** La pieza que se acaba de subir. `null` mientras no se suba ninguna. */
  const [subida, setSubida] = useState<ImagenSubidaDto | null>(null);
  const imagen = useSubidaImagen();
  /** La pieza para celular recien subida, y si se pidio quitar la que habia. */
  const [subidaMovil, setSubidaMovil] = useState<ImagenSubidaDto | null>(null);
  const [quitarMovil, setQuitarMovil] = useState(false);
  const imagenMovil = useSubidaImagen();

  // Al abrir se carga lo que hay: los campos del banner que se edita, o vacios
  // en un alta. Sin esto, editar el segundo banner mostraria el primero.
  useEffect(() => {
    if (!abierto) return;

    setUbicacion(banner?.ubicacion ?? 'carrusel');
    setTextoAlternativo(banner?.textoAlternativo ?? '');
    setTitulo(banner?.titulo ?? '');
    setBajada(banner?.bajada ?? '');
    setEnlace(banner?.enlace ?? '');
    // La pieza que ya tenia el banner no es una subida: se muestra desde el
    // propio banner. Esto queda en null hasta que alguien suba una nueva.
    setSubida(null);
    setSubidaMovil(null);
    setQuitarMovil(false);
  }, [abierto, banner]);

  /** Lo que se ve en la vista previa: la pieza nueva si la hay, si no la actual. */
  const previa =
    subida ??
    (banner !== null ? { url: banner.imagenUrl, ancho: banner.ancho, alto: banner.alto } : null);
  const puedeGuardar = subida !== null || banner !== null;
  const piezaMovil: VistaPreviaPieza | null =
    subidaMovil ?? (quitarMovil ? null : (banner?.imagenMovil ?? null));

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();

    const comunes = {
      ubicacion,
      titulo: vacioANulo(titulo),
      bajada: vacioANulo(bajada),
      textoAlternativo: textoAlternativo.trim(),
      enlace: vacioANulo(enlace),
      activo: banner?.activo ?? true,
    };

    // La pieza solo viaja si se subio una nueva. Omitida, el backend conserva
    // la que el banner ya tenia: para eso `ActualizarBannerDto` es parcial.
    const pieza =
      subida !== null ? { clave: subida.clave, ancho: subida.ancho, alto: subida.alto } : null;

    if (banner === null) {
      // Sin pieza no hay alta posible, y el boton de guardar ya esta apagado.
      if (pieza === null) return;
      onCrear({ ...comunes, ...pieza, ...movilDelDto() });
      return;
    }

    onActualizar({ ...comunes, ...(pieza ?? {}), ...movilDelDto() });
  }

  /**
   * La pieza para celular solo existe en el carrusel. Omitida, el backend
   * conserva la que habia; en `null`, la quita (tambien al pasar el banner a
   * otra fila, donde no se usaria).
   */
  function movilDelDto(): Pick<CrearBannerDto, 'claveMovil' | 'anchoMovil' | 'altoMovil'> {
    const sinPieza = { claveMovil: null, anchoMovil: null, altoMovil: null };
    if (ubicacion !== 'carrusel') return (banner?.imagenMovil ?? null) !== null ? sinPieza : {};
    if (subidaMovil !== null) {
      return {
        claveMovil: subidaMovil.clave,
        anchoMovil: subidaMovil.ancho,
        altoMovil: subidaMovil.alto,
      };
    }
    return quitarMovil ? sinPieza : {};
  }

  return (
    <Modal
      titulo={banner === null ? 'Agregar un banner' : 'Editar el banner'}
      bajada="Aparece en una de las tres filas de la portada"
      abierto={abierto}
      ancho="amplio"
      onCerrar={() => {
        if (!enviando) onCerrar();
      }}
    >
      <form className="formulario-admin" onSubmit={enviar} noValidate>
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {/* Dos columnas: las piezas a la izquierda y los textos a la derecha.
            En una sola, con las dos vistas previas, no cabia en la pantalla. */}
        <div className="formulario-columnas">
          <div>
            <div className="campo">
              <label htmlFor="banner-ubicacion">En qué fila va</label>
              <select
                id="banner-ubicacion"
                className="selector"
                aria-describedby="ayuda-ubicacion"
                value={ubicacion}
                onChange={(evento) => {
                  setUbicacion(evento.target.value as UbicacionBanner);
                }}
              >
                {UBICACIONES.map((fila) => (
                  <option key={fila} value={fila}>
                    {NOMBRE_UBICACION[fila]}
                  </option>
                ))}
              </select>
              <p className="campo-ayuda" id="ayuda-ubicacion">
                {AYUDA_UBICACION[ubicacion]}
              </p>
            </div>

            <div className="campo">
              <span className="campo-et">La pieza</span>

              {previa !== null ? (
                <div className="banner-previa">
                  <img src={previa.url} alt="" width={previa.ancho} height={previa.alto} />
                </div>
              ) : null}

              {imagen.falla !== null ? (
                <p className="acceso-error" role="alert">
                  <Icono nombre="alerta" />
                  <span>{imagen.falla}</span>
                </p>
              ) : null}

              <SubidorImagen
                etiqueta={banner === null ? 'elige un archivo' : 'reemplázala por otra'}
                subiendo={imagen.subiendo}
                onArchivo={(archivo) => {
                  void imagen.subir(async () => {
                    setSubida(await subirImagenBanner(archivo));
                  });
                }}
              />

              <p className="campo-ayuda" role="status">
                {previa !== null
                  ? `Mide ${String(previa.ancho)} x ${String(previa.alto)} px.`
                  : 'JPEG, PNG o WebP de hasta 12 MB. Se publica en WebP, con el lado mayor en 1920 px.'}
              </p>
            </div>

            {ubicacion === 'carrusel' ? (
              <CampoPiezaMovil
                pieza={piezaMovil}
                subida={imagenMovil}
                onArchivo={(archivo) => {
                  void imagenMovil.subir(async () => {
                    setSubidaMovil(await subirImagenBanner(archivo));
                    setQuitarMovil(false);
                  });
                }}
                onQuitar={() => {
                  setSubidaMovil(null);
                  setQuitarMovil(true);
                }}
              />
            ) : null}
          </div>

          <div>
            <div className="campo">
              <label htmlFor="banner-alt">Qué se ve en la imagen</label>
              <input
                id="banner-alt"
                required
                autoComplete="off"
                aria-describedby="ayuda-alt"
                value={textoAlternativo}
                onChange={(evento) => {
                  setTextoAlternativo(evento.target.value);
                }}
              />
              <p className="campo-ayuda" id="ayuda-alt">
                Lo lee en voz alta quien navega sin ver la pantalla, y es lo que Google entiende del
                banner. Describe la oferta; no escribas la palabra banner.
              </p>
            </div>

            <div className="campo">
              <label htmlFor="banner-titulo">Título (opcional)</label>
              <input
                id="banner-titulo"
                autoComplete="off"
                value={titulo}
                onChange={(evento) => {
                  setTitulo(evento.target.value);
                }}
              />
            </div>

            <div className="campo">
              <label htmlFor="banner-bajada">Bajada (opcional)</label>
              <input
                id="banner-bajada"
                autoComplete="off"
                value={bajada}
                onChange={(evento) => {
                  setBajada(evento.target.value);
                }}
              />
            </div>

            <div className="campo">
              <label htmlFor="banner-enlace">A dónde lleva (opcional)</label>
              <input
                id="banner-enlace"
                autoComplete="off"
                placeholder="/categoria/formones"
                aria-describedby="ayuda-enlace"
                value={enlace}
                onChange={(evento) => {
                  setEnlace(evento.target.value);
                }}
              />
              <p className="campo-ayuda" id="ayuda-enlace">
                Ruta dentro del sitio. Sin esto el banner se ve, pero no se puede pulsar.
              </p>
            </div>
          </div>
        </div>

        <div className="modal-acciones">
          <button type="button" className="btn btn-contorno" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primario"
            disabled={enviando || imagen.subiendo || imagenMovil.subiendo || !puedeGuardar}
            title={!puedeGuardar ? 'Falta subir la imagen del banner' : undefined}
          >
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
